import { useState, useEffect, useCallback } from 'react';
import { fetchPension, PensionRecord, PensionResponse } from '../api/pensions';

interface UserPensionsResult {
  pensionRecords: PensionRecord[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  searchQuery: string;
  pageSize: number;
  paginatedPensionRecords: PensionRecord[];
  setCurrentPage: (page: number) => void;
  setSearchQuery: (query: string) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
}

interface UsePensionsOptions {
  pageSize?: number;
  fromDate?: Date | null;
  toDate?: Date | null;
}

export const usePensions = (options: UsePensionsOptions = {}): UserPensionsResult => {
  const { pageSize = 10, fromDate, toDate } = options;
  
  const [pensionRecords, setPensionRecords] = useState<PensionRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchData = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      const response: PensionResponse = await fetchPension({
        PageNumber: page,
        PageSize: pageSize,
      });
      if (response && response.response) {
        setPensionRecords(response.response);
      }
    } catch (err) {
      console.error('Failed to fetch pensions:', err);
      setError((err as Error).message || 'Failed to load pensions');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  // Filter pensions based on search query and date range
  const filteredPensionRecords = pensionRecords.filter((record) => {
    // Search filter
    const matchesSearch = !searchQuery || 
      record.invoiceNumber?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.pensionAmount?.toString().includes(searchQuery) ||
      record.employeeName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesDateRange = true;
    if (record.createdDate && (fromDate || toDate)) {
      const recordDate = new Date(record.createdDate);
      
      if (fromDate) {
        const fromDateStart = new Date(fromDate);
        fromDateStart.setHours(0, 0, 0, 0);
        if (recordDate < fromDateStart) {
          matchesDateRange = false;
        }
      }
      
      if (toDate && matchesDateRange) {
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        if (recordDate > toDateEnd) {
          matchesDateRange = false;
        }
      }
    }
    
    return matchesSearch && matchesDateRange;
  });


  const totalPages = Math.ceil(filteredPensionRecords.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedPensionRecords = filteredPensionRecords.slice(startIndex, startIndex + pageSize);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, fromDate, toDate]);

  // Navigation handlers
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Refetch function
  const refetch = useCallback(async () => {
    await fetchData(currentPage);
  }, [currentPage, fetchData]);

  return {
    pensionRecords,
    loading,
    error,
    currentPage,
    totalPages,
    searchQuery,
    pageSize,
    paginatedPensionRecords,
    setCurrentPage,
    setSearchQuery,
    goToNextPage,
    goToPreviousPage,
    refetch,
  };
};
